import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '../lib/api';

/**
 * Admin password-reset landing page (admin_reset scope).
 *
 * Reached by clicking the link in the admin reset email:
 *   admin.dealerbosses.com/magic/<token> → 302 → this page with ?token=<token>
 *
 * POST /auth/reset peeks → looks up admin row → atomic consume + UPDATE
 * password. On success the server sets the admin_session cookie and we
 * pass control back up by calling onReset (mirrors Login's onLogin).
 */
export function Reset({ onReset }: { onReset: () => void }) {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) {
      setError(
        'Missing token — open this page from the link in your reset email.',
      );
    }
  }, [token]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!token) return;
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirm) {
      setError("Passwords don't match.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await api('/auth/reset', { method: 'POST', json: { token, password } });
      // Success: server set the admin_session cookie. Tell App to refresh.
      onReset();
    } catch (err: unknown) {
      const e = err as { status?: number; message?: string };
      if (e.status === 410) {
        setError(
          'This reset link has expired or already been used. Request a new one.',
        );
      } else if (e.status === 403) {
        setError(
          "Wrong link type — this page only accepts admin password reset links.",
        );
      } else if (e.status === 404) {
        setError(
          'No matching admin user. Contact support — the link is still valid.',
        );
      } else {
        setError(e.message || 'Could not reset password.');
      }
      setSubmitting(false);
    }
  }

  return (
    <div className="login-page">
      <div className="card login-card">
        <h1>Choose a new admin password</h1>
        <p className="muted small">
          Pick a password you haven't used here before. You'll be signed in
          automatically once it's set.
        </p>
        <form onSubmit={handleSubmit} autoComplete="off">
          <label htmlFor="password">New password</label>
          <input
            id="password"
            type="password"
            autoComplete="new-password"
            minLength={8}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="At least 8 characters"
            required
            disabled={submitting || !token}
          />
          <label htmlFor="confirm">Confirm password</label>
          <input
            id="confirm"
            type="password"
            autoComplete="new-password"
            minLength={8}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            required
            disabled={submitting || !token}
          />
          {error && (
            <div className="banner err" style={{ marginTop: 12 }}>
              {error}
            </div>
          )}
          <div className="button-row" style={{ marginTop: 16 }}>
            <button type="submit" disabled={submitting || !token}>
              {submitting ? 'Updating…' : 'Set new password'}
            </button>
          </div>
          <div style={{ marginTop: 16, fontSize: 13 }}>
            <Link to="/forgot">Request a new link</Link>
          </div>
        </form>
      </div>
    </div>
  );
}
